import {
  Alert,
  Box,
  Button,
  Container,
  Fab,
  Grid,
  Paper,
  Toolbar,
  Tooltip,
} from '@mui/material';
import Dashboard from '../../../../components/Admin/Dashboard';
import { Styles } from '../../../../styles/global';
import { useEffect, useRef, useState } from 'react';
import {
  getRubrics,
  createRubric,
  objectToQueryString,
} from '../../../../utils/API';
import { useParams, useSearchParams } from 'react-router-dom';
import Filters from '../../../../components/Filters';
import rubricFiltersSchema from '../../../../utils/FiltersSchema/AdminRubrics';
import ProjectsView from '../../../../components/Views';
import { AddCard } from '@mui/icons-material';
import AdvancedModal from '../../../../components/AdvancedModal';
import RubricForm from '../../../../components/Rubrics';
import { useFeedback } from '../../../../utils/FeedbackContext';
import FacultyDashboard from '../../../../components/Faculty/Dashboard';
import StudentDashboard from '../../../../components/Student/Dashboard';
import JudgeDashboard from '../../../../components/Judge/Dashboard';
import { useAuth } from '../../../../utils/AuthContext';

function FiltersSidebar() {
  const [rubric, setRubric] = useState({
    title: '',
    description: '',
    type: '',
    department: '',
    course: '',
    numOfScoreCategories: 4,
    numOfRubricCategories: 4,
    weights: [1, 1, 1, 1],
    rubricCategories: ['', '', '', ''],
    scoreCategories: ['', '', '', ''],
    scoreCategoriesDescriptions: [
      ['', '', '', ''],
      ['', '', '', ''],
      ['', '', '', ''],
      ['', '', '', ''],
    ],
  });
  const [open, setOpen] = useState(false);
  const [error, setError] = useState();
  const [searchParams] = useSearchParams();
  const { notifySuccess, notifyError } = useFeedback();
  const handleSave = async () => {
    try {
      let params = {};
      for (const [key, value] of searchParams.entries()) {
        params[key] = value;
      }

      // convert rubric.weights to a number
      rubric.weights = rubric.weights.map((weight) => Number(weight));

      await createRubric({ ...params, ...rubric });
      notifySuccess('Rubric created successfully');
      setOpen(false);

      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      notifyError(
        'Failed to create rubric, check error message for more details'
      );
      setError(error?.response?.data);
    }
  };

  return (
    <Box {...Styles.dashboardContentWidget('auto')}>
      <Filters filtersSchema={rubricFiltersSchema()} searchType="rubrics" />
      <br />
      <Box textAlign="center">
        <Tooltip title="Create Rubric" aria-label="Create Rubric">
          <Button
            variant="contained"
            color="primary"
            aria-label="add"
            onClick={() => setOpen(true)}
          >
            <Box display="flex" gap={1}>
              <AddCard />
              Rubric
            </Box>
          </Button>
        </Tooltip>
        <AdvancedModal
          open={open}
          handleClose={() => setOpen(false)}
          handleSave={handleSave}
          title="Create a Rubric"
          width={'80vw'}
        >
          <RubricForm rubric={rubric} setRubric={setRubric} />
          {error && (
            <Alert severity="error" sx={{ marginY: 1 }}>
              {error?.errMessage}
            </Alert>
          )}
        </AdvancedModal>
      </Box>
    </Box>
  );
}

function Rubrics() {
  const [rubrics, setRubrics] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const topRef = useRef(null); // Create a ref for the top of the page
  const acceptableParams = ['department', 'course', 'type', 'limit', 'page'];
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || false;
  useEffect(() => {
    if (isAdmin) {
      if (
        !searchParams.get('search') ||
        searchParams.get('search') !== 'rubrics'
      ) {
        setSearchParams('search=rubrics');
      }
      if (!searchParams.get('limit')) {
        setSearchParams(`${searchParams.toString()}&limit=10`);
      }
      if (!searchParams.get('page')) {
        setSearchParams(`${searchParams.toString()}&page=1`);
      }
    }
  }, [isAdmin, searchParams, setSearchParams]);

  useEffect(() => {
    async function fetchRubrics() {
      try {
        const params = {};
        for (const [key, value] of searchParams.entries()) {
          if (acceptableParams.includes(key)) {
            params[key] = value;
          }
        }
        const rubricsResponse = await getRubrics(objectToQueryString(params));
        setRubrics(rubricsResponse?.data || []);
      } catch (error) {
        console.log(error);
      }
    }
    try {
      if (isAdmin) {
        if (!searchParams.get('limit') || !searchParams.get('page')) {
          return;
        }
      }
      if (!window.location.href.includes('admin') || isAdmin) {
        fetchRubrics();
      }
    } catch (error) {
      console.log(error);
    }
  }, [searchParams, isAdmin]);

  let link = '';
  if (isAdmin) {
    link = `/admin/dashboard/rubrics`;
  }
  if (user?.role === 'FACULTY') {
    link = `/faculty/dashboard/rubrics`;
  }

  if (user?.role === 'JUDGE') {
    link = `/judge/dashboard/rubrics`;
  }

  const { pid } = useParams();
  if (user?.role === 'STUDENT') {
    link = `/student/dashboard/project/${pid}/rubrics`;
  }

  return (
    <>
      <Box {...Styles.mainBox}>
        <Toolbar />

        {/* Container With Max Width = lg breakpoint */}
        <Container {...Styles.customContainer('xl')}>
          {/*Outer Grid Container */}
          <Grid container spacing={3}>
            {/*Inner Grid Begins*/}

            {/*Inner Grid Item 0: Space Taken */}
            <Grid item lg={isAdmin ? 3 : 0} sm={0} xs={0} ref={topRef}>
              <div />
            </Grid>

            {/*Inner Grid Item 1: Page Title */}
            <Grid item lg={isAdmin ? 9 : 12} sm={12} xs={12} ref={topRef}>
              <Paper {...Styles.dashboardPageWidget()}>Rubrics</Paper>
            </Grid>

            {/*Inner Grid Item 2: Filters */}
            {isAdmin && (
              <Grid item lg={3} sm={12} xs={12}>
                <FiltersSidebar />
              </Grid>
            )}

            {/*Inner Grid Item 3: Projects View */}
            <Grid item lg={isAdmin ? 9 : 12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentMinWidget()}>
                <ProjectsView
                  items={rubrics}
                  topRef={topRef}
                  link={link}
                  itemType={'rubrics'}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default function FacultyDashboardRubrics() {
  const { user } = useAuth();

  if (!user) {
    return <></>;
  }

  return (
    <>
      {user?.role === 'ADMIN' && (
        <Dashboard Page={Rubrics} PageName="(A) Dashboard - Rubric" />
      )}
      {user?.role === 'FACULTY' && (
        <FacultyDashboard Page={Rubrics} PageName="(F) Dashboard - Rubric" />
      )}
      {user?.role === 'STUDENT' && (
        <StudentDashboard Page={Rubrics} PageName="(S) Dashboard - Rubric" />
      )}
      {user?.role === 'JUDGE' && (
        <JudgeDashboard Page={Rubrics} PageName="(J) Dashboard - Rubric" />
      )}
    </>
  );
}
